import { TableExpandedStateManager } from '@/lib/utils/table-expanded-state';
import type { ExpandedState } from '@tanstack/react-table';
import { atom } from 'jotai';

/**
 * Expanded state atom with localStorage persistence.
 * Manages which table rows are expanded/collapsed.
 * State is automatically saved to localStorage on every write.
 */
const getInitialExpandedState = (): ExpandedState => {
  return TableExpandedStateManager.getExpandedState();
};

// Base atom initialized from localStorage
const baseExpandedStateAtom = atom<ExpandedState>(getInitialExpandedState());

/**
 * Main expanded state atom with automatic localStorage persistence.
 * Handles both boolean values (true = all expanded, false = all collapsed)
 * and object values (specific rows expanded).
 * Empty objects ({}) and false values clear localStorage.
 * Supports updater functions from TanStack Table's OnChangeFn.
 */
export const expandedState = atom<
  ExpandedState,
  [ExpandedState | ((prev: ExpandedState) => ExpandedState)],
  void
>(
  (get) => get(baseExpandedStateAtom),
  (
    get,
    set,
    updater: ExpandedState | ((prev: ExpandedState) => ExpandedState)
  ) => {
    const currentState = get(baseExpandedStateAtom);
    const newState =
      typeof updater === 'function' ? updater(currentState) : updater;
    set(baseExpandedStateAtom, newState);

    // Persist to localStorage
    const isEmptyObject =
      typeof newState === 'object' &&
      newState !== null &&
      Object.keys(newState).length === 0;
    const isCollapsedState =
      (typeof newState === 'boolean' && !newState) || isEmptyObject;

    if (isCollapsedState) {
      TableExpandedStateManager.clearExpandedState();
    } else {
      TableExpandedStateManager.setExpandedState(newState);
    }
  }
);
